import React, { useState, useEffect } from "react";
import { useTranslation } from "../../../../../hooks";
import styles from "./TimetableWidget.module.css";

const TimetableWidget = () => {
	const { t } = useTranslation();
	const today = new Date();
	const isFriday = today.getDay() === 5;
	const [isRamadan, setIsRamadan] = useState(false);
	const [currentTime, setCurrentTime] = useState(new Date());

	// Determine winter vs summer (April 7 rule)
	const month = today.getMonth() + 1;
	const day = today.getDate();
	const isWinter = month < 4 || (month === 4 && day < 7) || month >= 10;

	const [mode, setMode] = useState(isWinter ? "Winter" : "Summer");
	const [subMode, setSubMode] = useState(
		isFriday ? "Friday" : "Monday to Thursday"
	);

	useEffect(() => {
		const timer = setInterval(() => setCurrentTime(new Date()), 60000);
		return () => clearInterval(timer);
	}, []);

	// Detect Ramadan using Aladhan API
	useEffect(() => {
		const checkRamadan = async () => {
			try {
				const res = await fetch(
					`https://api.aladhan.com/v1/gToH/${today.getDate()}-${
						today.getMonth() + 1
					}-${today.getFullYear()}`
				);
				const data = await res.json();
				if (data?.data?.hijri?.month?.en === "Ramadan") {
					setIsRamadan(true);
					setMode("Ramadan");
				}
			} catch {
				setIsRamadan(false);
			}
		};
		checkRamadan();
	}, [today]);

	const getTranslatedLabel = (labelKey) => {
		switch (labelKey) {
			case "1st Period":
				return t("timetable.periods.first");
			case "2nd Period":
				return t("timetable.periods.second");
			case "3rd Period":
				return t("timetable.periods.third");
			case "4th Period":
				return t("timetable.periods.fourth");
			case "5th Period":
				return t("timetable.periods.fifth");
			case "6th Period":
				return t("timetable.periods.sixth");
			case "7th Period":
				return t("timetable.periods.seventh");
			case "Break":
				return t("timetable.breaks.main");
			case "Prayer":
				return t("timetable.breaks.main");
			default:
				return labelKey;
		}
	};

	const timetableData = {
		Winter: {
			"Monday to Thursday": [
				{ start: "08:30", end: "09:10", label: "1st Period" },
				{ start: "09:15", end: "09:55", label: "2nd Period" },
				{ start: "10:00", end: "10:40", label: "3rd Period" },
				{ start: "10:40", end: "11:00", label: "Break" },
				{ start: "11:05", end: "11:45", label: "4th Period" },
				{ start: "11:50", end: "12:30", label: "5th Period" },
				{ start: "12:35", end: "12:50", label: "Prayer" },
				{ start: "12:55", end: "13:35", label: "6th Period" },
				{ start: "13:40", end: "14:20", label: "7th Period" },
			],
			Friday: [
				{ start: "08:20", end: "08:50", label: "1st Period" },
				{ start: "08:55", end: "09:25", label: "2nd Period" },
				{ start: "09:30", end: "10:00", label: "3rd Period" },
				{ start: "10:00", end: "10:15", label: "Break" },
				{ start: "10:20", end: "10:50", label: "4th Period" },
				{ start: "10:55", end: "11:25", label: "5th Period" },
			],
		},

		Summer: {
			"Monday to Thursday": [
				{ start: "08:30", end: "09:10", label: "1st Period" },
				{ start: "09:15", end: "09:55", label: "2nd Period" },
				{ start: "10:00", end: "10:40", label: "3rd Period" },
				{ start: "10:40", end: "11:00", label: "Break" },
				{ start: "11:05", end: "11:45", label: "4th Period" },
				{ start: "11:50", end: "12:30", label: "5th Period" },
				{ start: "12:35", end: "13:15", label: "6th Period" },
				{ start: "13:20", end: "13:35", label: "Break" },
				{ start: "13:40", end: "14:20", label: "7th Period" },
			],
			Friday: [
				{ start: "08:30", end: "09:10", label: "1st Period" },
				{ start: "09:15", end: "09:55", label: "2nd Period" },
				{ start: "10:00", end: "10:40", label: "3rd Period" },
				{ start: "10:40", end: "11:00", label: "Break" },
				{ start: "11:05", end: "11:45", label: "4th Period" },
				{ start: "11:50", end: "12:30", label: "5th Period" },
			],
		},

		Ramadan: {
			"Monday to Thursday": [
				{ start: "08:30", end: "09:00", label: "1st Period" },
				{ start: "09:05", end: "09:35", label: "2nd Period" },
				{ start: "09:40", end: "10:10", label: "3rd Period" },
				{ start: "10:10", end: "10:25", label: "Break" },
				{ start: "10:30", end: "11:00", label: "4th Period" },
				{ start: "11:05", end: "11:35", label: "5th Period" },
				{ start: "11:40", end: "11:50", label: "Prayer" },
				{ start: "11:55", end: "12:25", label: "6th Period" },
				{ start: "12:30", end: "13:00", label: "7th Period" },
			],
			Friday: [
				{ start: "08:20", end: "08:50", label: "1st Period" },
				{ start: "08:55", end: "09:25", label: "2nd Period" },
				{ start: "09:30", end: "10:00", label: "3rd Period" },
				{ start: "10:00", end: "10:10", label: "Break" },
				{ start: "10:15", end: "10:45", label: "4th Period" },
				{ start: "10:50", end: "11:20", label: "5th Period" },
			],
		},
	};

	const currentTimetable = timetableData[mode][subMode];

	const isCurrentPeriod = (start, end) => {
		const now = currentTime.getHours() * 60 + currentTime.getMinutes();
		const [sH, sM] = start.split(":").map(Number);
		const [eH, eM] = end.split(":").map(Number);
		return now >= sH * 60 + sM && now < eH * 60 + eM;
	};

	return (
		<div className={styles.timetableWidget}>
			<div className={styles.timetable}>
				{currentTimetable.map((item, index) => (
					<div
						key={index}
						className={`${styles.timetableRow} ${
							isCurrentPeriod(item.start, item.end) ? styles.current : ""
						}`}
					>
						<div className={styles.timetableTime}>
							{item.start} - {item.end}
						</div>
						<div className={styles.timetableLabel}>
							{getTranslatedLabel(item.label)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default TimetableWidget;
